import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

interface ProtectedRouteProps {
  component: React.ComponentType<any>;
  pageKey: string;
  [key: string]: any;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ component: Component, pageKey, ...rest }) => {
  const [isPublished, setIsPublished] = useState<boolean | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);

        const [{ data: { user } }, { data: pubData, error: pubError }] = await Promise.all([
          supabase.auth.getUser(),
          supabase
            .from('system_settings')
            .select('value')
            .eq('key', `${pageKey}_published`)
            .maybeSingle(),
        ]);

        if (pubError) {
          setIsPublished(pubError.message.includes('No rows found'));
        } else {
          setIsPublished(pubData?.value === true || pubData?.value === 'true' || pubData?.value == null);
        }

        if (!user) {
          setIsAdmin(false);
          return;
        }

        const { data: authUser, error: roleError } = await supabase
          .from('auth_users')
          .select('role')
          .eq('id', user.id)
          .maybeSingle();

        const adminRoles = ['admin', 'super_admin', 'user', 'manager', 'editor', 'technical'];
        setIsAdmin(!roleError && adminRoles.includes(authUser?.role ?? ''));
      } catch {
        setIsPublished(false);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [pageKey]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent"></div>
      </div>
    );
  }

  // Allow access if the page is published or the user is an admin
  if (isPublished || isAdmin) {
    return <Component {...rest} />;
  }

  // Redirect to home page if not published and not admin
  return <Navigate to="/" replace />;
};

export default ProtectedRoute;